import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaSave } from "react-icons/fa";
import { FiMinus, FiPlus } from "react-icons/fi";
import LocationNew from "./LocationNew";
import ConverterNew from "./ConverterNew";
import SensorNew from "./SensorNew";
import api from '../../api/manage_database/FlowMeterReq';
import BackButton from "../../components/BackButton";
import SelectLocation from "../../components/SelectLocation";
import SelectConverter from "../../components/SelectConverter";
import SelectSensor from "../../components/SelectSensor";
import SelectDataframeType from "../../components/SelectDataframeType";
import { toast } from 'sonner';
import dataTransmissionApi from '../../api/DataTransmissionReq';

const FlowMeterNew = () => {

    const [converters, setConverters] = useState([]);
    const [selectedConverter, setSelectedConverter] = useState('');
    const [showConverterForm, setShowConverterForm] = useState(false);
    const [addingConverter, setAddingConverter] = useState(false);

    const [sensors, setSensors] = useState([]);
    const [selectedSensor, setSelectedSensor] = useState('');
    const [showSensorForm, setShowSensorForm] = useState(false);
    const [addingSensor, setAddingSensor] = useState(false);

    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [showLocationForm, setShowLocationForm] = useState(false);
    const [addingLocation, setAddingLocation] = useState(false);

    const [dataframeTypes, setDataframeTypes] = useState([]);
    const [selectedDataframeType, setSelectedDataframeType] = useState('');
    
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const deviceRegistrationId = searchParams.get('device_registration_id');

    useEffect(() => {
        const fetchConverters = async () => {
            try {
                const response = await api.get('available-converters', {withCredentials: true});
                if (response.data.success) {
                    setConverters(response.data.payload)
                } else {
                    toast.error('Not got data from database');
                }
                
            } catch (err) {
                console.log(err.response);
            }
        };

        fetchConverters();
    }, []);

    useEffect(() => {
        const fetchSensors = async () => {
            try {
                const response = await api.get('available-sensors', {withCredentials: true});
                if (response.data.success) {
                    setSensors(response.data.payload);
                } else {
                    toast.error('Not got data from database');
                }
                
            } catch (err) {
                console.log(err.response);
            }
        };

        fetchSensors();
    }, []);

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await api.get('available-locations', {withCredentials: true});
                if (response.data.success) {
                    setLocations(response.data.payload);
                } else {
                    toast.error('Not got data from database');
                }
            } catch (err) {
                console.log(err.response);
            }
        };

        fetchLocations();
    }, []);
    
    useEffect(() => {
        const fetchDataframeTypes = async () => {
            try {
                const response = await api.get('available-dataframe-types', {withCredentials: true});
                if (response.data.success) {
                    setDataframeTypes(response.data.payload.types);
                }
            } catch (err) {
                console.log(err.response);
            }
        };

        fetchDataframeTypes();
    }, []);

    const handleAdd = async () => {

        if (selectedLocation === '') {
            toast.error('Set available location!')
            return
        }

        if (selectedConverter === '') {
            toast.error('Set available converter!')
            return
        }

        if (selectedSensor === '') {
            toast.error('Set available sensor!')
            return
        }

        if (selectedDataframeType === '') {
            toast.error('Set available Data Frame Type!')
            return
        }
        try {
            const response = await api.post(
                '',
                {
                    converter_id: selectedConverter,
                    sensor_id: selectedSensor,
                    location_id: selectedLocation,
                    dataframe_type: selectedDataframeType
                },
                {
                    withCredentials: true
                }
            );
            if (response.data.success) {
                const createdFlowMeterId = response.data.payload[0].id;

                if (deviceRegistrationId) {
                    try {
                        const assignmentResponse = await dataTransmissionApi.put(
                            `device/${deviceRegistrationId}/assignment`,
                            {flow_meter_id: createdFlowMeterId},
                            {withCredentials: true}
                        );
                        if (!assignmentResponse.data.success) {
                            toast.error(assignmentResponse.data.message);
                            navigate(`/device-registration/${deviceRegistrationId}/assign`);
                            return;
                        }
                    } catch (assignmentError) {
                        toast.error(
                            assignmentError.response?.data?.message ||
                            'Flow Meter was created, but the device could not be assigned'
                        );
                        navigate(`/device-registration/${deviceRegistrationId}/assign`);
                        return;
                    }
                }

                toast.success(response.data.message);
                navigate(
                    deviceRegistrationId
                        ? '/data-transmission'
                        : `../record/${createdFlowMeterId}`,
                    {
                        state: {
                            addRecordMessage: 'Added record to database!',
                            anotherTypeOfPage: true
                        }
                    }
                )
            } else {
                toast.error(response.data.message)
            }
        } catch (err) {
            console.log(err.response)
        }
    };

    return (
        <div className={`flow-meter-new-container${showLocationForm || showConverterForm || showSensorForm ? ' flow-meter-new-container-expanded' : ''}`}>
            <h2 className='flow-meter-new-title'>Flow Meter</h2>
            <div className='flow-meter-new-card'>
                <div className="flow-meter-new-label-select-wrapper">
                    <label
                        htmlFor='location-select'
                        className='bold-label'
                    >
                        Location:
                    </label>
                    <div className="flow-meter-new-location-choice">
                        <SelectLocation
                            Locations={locations}
                            selectedLocation={selectedLocation}
                            setSelectedLocation={setSelectedLocation}
                        />
                        <button
                            type="button"
                            className="manage-database-button-add flow-meter-new-location-toggle"
                            aria-label={showLocationForm ? 'Hide location form' : 'Add location'}
                            title={showLocationForm ? 'Hide location form' : 'Add location'}
                            aria-expanded={showLocationForm}
                            aria-controls="flow-meter-location-form"
                            disabled={addingLocation}
                            onClick={() => setShowLocationForm((previous) => !previous)}
                        >
                            {showLocationForm ? <FiMinus /> : <FiPlus />}
                        </button>
                    </div>
                </div>
                <div className="flow-meter-new-label-select-wrapper">
                    <label
                        htmlFor='converter-select'
                        className='bold-label'
                    >
                        Converter:
                    </label>
                    <div className="flow-meter-new-related-choice">
                        <SelectConverter
                            Converters={converters}
                            selectedConverter={selectedConverter}
                            setSelectedConverter={setSelectedConverter}
                        />
                        <button
                            type="button"
                            className="manage-database-button-add flow-meter-new-related-toggle"
                            aria-label={showConverterForm ? 'Hide converter form' : 'Add converter'}
                            title={showConverterForm ? 'Hide converter form' : 'Add converter'}
                            aria-expanded={showConverterForm}
                            aria-controls="flow-meter-converter-form"
                            disabled={addingConverter}
                            onClick={() => setShowConverterForm((previous) => !previous)}
                        >
                            {showConverterForm ? <FiMinus /> : <FiPlus />}
                        </button>
                    </div>
                </div>
                <div className="flow-meter-new-label-select-wrapper">
                    <label
                        htmlFor='sensor-select'
                        className='bold-label'
                    >
                        Sensor:
                    </label>
                    <div className="flow-meter-new-related-choice">
                        <SelectSensor
                            Sensors={sensors}
                            selectedSensor={selectedSensor}
                            setSelectedSensor={setSelectedSensor}
                        />
                        <button
                            type="button"
                            className="manage-database-button-add flow-meter-new-related-toggle"
                            aria-label={showSensorForm ? 'Hide sensor form' : 'Add sensor'}
                            title={showSensorForm ? 'Hide sensor form' : 'Add sensor'}
                            aria-expanded={showSensorForm}
                            aria-controls="flow-meter-sensor-form"
                            disabled={addingSensor}
                            onClick={() => setShowSensorForm((previous) => !previous)}
                        >
                            {showSensorForm ? <FiMinus /> : <FiPlus />}
                        </button>
                    </div>
                </div>
                <div className="flow-meter-new-label-select-wrapper">
                    <label
                        htmlFor='flow-meter-select'
                        className='bold-label'
                    >
                        Dataframe:
                    </label>
                    <SelectDataframeType
                        DataframeTypes={dataframeTypes}
                        selectedDataframeType={selectedDataframeType}
                        setSelectedDataframeType={setSelectedDataframeType}
                    />
                </div>
                <button className='flow-meter-new-button' onClick={handleAdd} disabled={addingLocation || addingConverter || addingSensor}>Add &nbsp; <FaSave/></button>
            </div>
            {showLocationForm && (
                <div id="flow-meter-location-form" className="flow-meter-location-panel">
                    <LocationNew
                        embedded
                        onSavingChange={setAddingLocation}
                        onCreated={(location) => {
                            setLocations((previous) => [...previous.filter((item) => item.id !== location.id), location]);
                            setSelectedLocation(location.id);
                            setShowLocationForm(false);
                        }}
                    />
                </div>
            )}
            {showConverterForm && (
                <div id="flow-meter-converter-form" className="flow-meter-related-panel">
                    <ConverterNew
                        embedded
                        onSavingChange={setAddingConverter}
                        onCreated={(converter) => {
                            setConverters((previous) => [...previous.filter((item) => item.id !== converter.id), converter]);
                            setSelectedConverter(converter.id);
                            setShowConverterForm(false);
                        }}
                    />
                </div>
            )}
            {showSensorForm && (
                <div id="flow-meter-sensor-form" className="flow-meter-related-panel">
                    <SensorNew
                        embedded
                        onSavingChange={setAddingSensor}
                        onCreated={(sensor) => {
                            setSensors((previous) => [...previous.filter((item) => item.id !== sensor.id), sensor]);
                            setSelectedSensor(sensor.id);
                            setShowSensorForm(false);
                        }}
                    />
                </div>
            )}
            <BackButton
                navigateTo={
                    deviceRegistrationId
                        ? `/device-registration/${deviceRegistrationId}/assign`
                        : '/manage-database/'
                }
            />
        </div>
    );
        
};

export default FlowMeterNew;
